/**
 * CoverageHeatmap Component
 *
 * Displays a heatmap grid of role capacity across dates
 */

import { useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { CapacitySnapshot } from '../types/capacity.types';
import { CoverageMetricType } from '../types/metrics.types';
import CoverageHeatmapCell from './CoverageHeatmapCell';
import { getUniqueRoles } from '../utils/capacityMetrics';
import { buildExtendedRoleCapacity } from '../engine/advancedMetrics';

interface CoverageHeatmapProps {
  snapshot: CapacitySnapshot;
  maxDays?: number; // Limit number of days to display
  metricType: CoverageMetricType;
}

export default function CoverageHeatmap({ snapshot, maxDays = 14, metricType }: CoverageHeatmapProps) {
  // Get unique roles
  const roles = useMemo(() => getUniqueRoles(snapshot), [snapshot]);

  // Build extended role capacity with advanced metrics
  const extendedRoleCapacity = useMemo(() => {
    return buildExtendedRoleCapacity(snapshot.roles, snapshot.providers);
  }, [snapshot.roles, snapshot.providers]);

  // Get unique dates (limit to maxDays)
  const dates = useMemo(() => {
    const uniqueDates = Array.from(
      new Set(extendedRoleCapacity.map((r) => r.date))
    ).sort();
    return uniqueDates.slice(0, maxDays);
  }, [extendedRoleCapacity, maxDays]);

  // Create a map for quick role-day lookup
  const roleCapacityMap = useMemo(() => {
    const map = new Map<string, typeof extendedRoleCapacity[0]>();
    extendedRoleCapacity.forEach((roleDay) => {
      const key = `${roleDay.role}|${roleDay.date}`;
      map.set(key, roleDay);
    });
    return map;
  }, [extendedRoleCapacity]);

  // Create a map for provider details lookup
  const providerDetailsMap = useMemo(() => {
    const map = new Map<string, typeof snapshot.providers>();
    snapshot.providers.forEach((provider) => {
      const key = `${provider.role}|${provider.date}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(provider);
    });
    return map;
  }, [snapshot.providers]);

  if (roles.length === 0 || dates.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No coverage data available
        </Typography>
      </Box>
    );
  }

  // Calculate dynamic cell sizing based on number of days
  // Use min/max widths to allow cells to grow on larger screens
  const getCellConfig = () => {
    if (dates.length <= 7) return { min: 90, max: 140, label: 60 };
    if (dates.length <= 14) return { min: 70, max: 110, label: 55 };
    if (dates.length <= 21) return { min: 50, max: 80, label: 50 };
    return { min: 42, max: 70, label: 50 }; // 30 days
  };

  const cellConfig = getCellConfig();
  const cellWidth = cellConfig.min; // Use min as base for compact view flag
  const roleLabelWidth = cellConfig.label;

  return (
    <Paper sx={{ p: 2, overflowX: 'auto' }}>
      {/* Legend */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Legend:
        </Typography>
        {metricType === 'visitCapacity' ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: 'rgba(76, 175, 80, 0.3)', border: '2px solid #4caf50' }} />
              <Typography variant="caption">2+ visits</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: 'rgba(255, 152, 0, 0.3)', border: '2px solid #ff9800' }} />
              <Typography variant="caption">1 visit</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: 'rgba(244, 67, 54, 0.3)', border: '2px solid #f44336' }} />
              <Typography variant="caption">&lt;1 visit</Typography>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: 'rgba(76, 175, 80, 0.3)', border: '2px solid #4caf50' }} />
              <Typography variant="caption">&lt;70% utilization</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: 'rgba(255, 152, 0, 0.3)', border: '2px solid #ff9800' }} />
              <Typography variant="caption">70-80% utilization</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: 'rgba(244, 67, 54, 0.3)', border: '2px solid #f44336' }} />
              <Typography variant="caption">&gt;80% utilization</Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Heatmap Grid - Responsive Layout */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Header Row (Dates) */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Box sx={{ minWidth: roleLabelWidth, width: roleLabelWidth, mr: 0.5, flexShrink: 0 }} /> {/* Spacer for role labels */}
          <Box sx={{ display: 'flex', flex: 1, gap: 0.5 }}>
            {dates.map((date) => (
              <Box
                key={date}
                sx={{
                  minWidth: cellConfig.min,
                  maxWidth: cellConfig.max,
                  flex: '1 1 auto',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 0.5,
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: cellWidth < 55 ? '0.6rem' : '0.75rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                  {new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: cellWidth < 55 ? '0.55rem' : '0.7rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                  {new Date(date).toLocaleDateString('en-US', {
                    weekday: 'short',
                  })}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Data Rows */}
        {roles.map((role) => (
          <Box key={role} sx={{ display: 'flex', mb: 0.5 }}>
            {/* Role Label */}
            <Box
              sx={{
                minWidth: roleLabelWidth,
                width: roleLabelWidth,
                mr: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                {role}
              </Typography>
            </Box>

            {/* Cells Container - Responsive */}
            <Box sx={{ display: 'flex', flex: 1, gap: 0.5 }}>
              {dates.map((date) => {
                const key = `${role}|${date}`;
                const roleDay = roleCapacityMap.get(key);
                const providerDetails = providerDetailsMap.get(key) || [];

                if (!roleDay) {
                  // No data for this role-date combination
                  return (
                    <Box
                      key={date}
                      sx={{
                        minWidth: cellConfig.min,
                        maxWidth: cellConfig.max,
                        minHeight: cellWidth < 55 ? 48 : 60,
                        flex: '1 1 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        border: '1px dashed #ccc',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        N/A
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <Box
                    key={date}
                    sx={{
                      minWidth: cellConfig.min,
                      maxWidth: cellConfig.max,
                      flex: '1 1 auto',
                    }}
                  >
                    <CoverageHeatmapCell
                      roleDay={roleDay}
                      providerDetails={providerDetails}
                      metricType={metricType}
                      cellWidth={cellWidth}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
